import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { RefreshCw } from "lucide-react";
import { api, qs } from "@/lib/api";
import { plainConfirmMessage, usePreviewConfirm } from "@/lib/confirmFlow";
import { formatCents, formatDate } from "@/lib/format";
import { useIsAdmin } from "@/lib/session";
import { useToast } from "@/components/Toaster";
import { Empty, PageHeader } from "@/components/PageHeader";
import { Facts, Problem, ServerSays } from "@/components/PreviewBits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { addressLines, HOLD_DAYS, methodLabel, PARTNER_STATUS, type PayoutAddress } from "./affiliates";

/** One partner in payouts_due (internal/affiliates/friday.go PayoutDue). */
export interface PayoutDue {
  affiliate_id: number;
  code: string;
  contact_id: number;
  name: string;
  email?: string;
  status: string;
  payable_cents: number;
  held_cents: number;
  next_payable_on?: string;
  payout_method?: string;
  paypal_email?: string;
  mailing_address?: PayoutAddress;
  missing_details: boolean;
}

export interface PayoutsDueOut {
  as_of: string;
  hold_days: number;
  partners: PayoutDue[];
  payable_cents: number;
  held_cents: number;
}

/** How to pay them, in one line ("PayPal: pat@example.com", "Check to 1 Main St, Gunnison, CO 81230"), or what's missing. */
export function payTo(p: PayoutDue): string {
  if (p.missing_details) {
    if (!p.payout_method) return "Missing: they haven't said how to be paid";
    if (p.payout_method === "paypal") return "Missing: PayPal email";
    if (p.payout_method === "check") return "Missing: mailing address";
    return "Missing payout details";
  }
  if (p.payout_method === "paypal") return `PayPal: ${p.paypal_email}`;
  if (p.payout_method === "check") return `Check to ${addressLines(p.mailing_address).join(", ")}`;
  return methodLabel(p.payout_method);
}

/** Billing → Partners: who to pay now, what is still held, and bringing in partners made in Keap. Read-only except the sync. */
export function PartnersToPayPage() {
  const isAdmin = useIsAdmin();
  const [held, setHeld] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const q = useQuery({ queryKey: ["payouts-due", held], queryFn: () => api<PayoutsDueOut>("/api/v1/payouts/due" + qs({ include_held: held || undefined })) });
  const rows = [...(q.data?.partners ?? [])].sort((a, b) => b.payable_cents - a.payable_cents || b.held_cents - a.held_cents);
  const payableNow = rows.filter((r) => r.payable_cents > 0);
  const missing = payableNow.filter((r) => r.missing_details).length;
  return (
    <div className="mx-auto max-w-[1200px] space-y-3">
      <PageHeader
        title="Partners: who to pay"
        sub="Referral partner commissions the CRM owes"
        actions={
          <>
            <Link to="/affiliates" className="text-sm text-[var(--color-primary)] hover:underline">
              All partners
            </Link>
            {isAdmin && (
              <Button size="sm" variant="outline" onClick={() => setSyncing(true)}>
                <RefreshCw className="size-3.5" /> Sync partners from Keap
              </Button>
            )}
          </>
        }
      >
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Commissions wait {HOLD_DAYS} days for refunds, then they can be paid. Send the money yourself (PayPal or a check), then open the partner and record the payout there.
          Money partners earned through Keap before the move is paid from Keap's report, not here. Dates are Mountain Time.
        </p>
      </PageHeader>
      {q.data && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Card className="p-3">
            <div className="text-xs text-[var(--color-muted-foreground)]">Payable now</div>
            <div className="num text-lg font-semibold">{formatCents(q.data.payable_cents)}</div>
            <div className="text-xs text-[var(--color-muted-foreground)]">
              {payableNow.length} partner{payableNow.length === 1 ? "" : "s"}
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-xs text-[var(--color-muted-foreground)]">Still held (inside {q.data.hold_days} days)</div>
            <div className="num text-lg font-semibold">{held ? formatCents(q.data.held_cents) : "not shown"}</div>
          </Card>
          <Card className="p-3">
            <div className="text-xs text-[var(--color-muted-foreground)]">Payout details missing</div>
            <div className={`num text-lg font-semibold ${missing ? "text-[var(--color-warning-fg)]" : ""}`}>{missing}</div>
            <div className="text-xs text-[var(--color-muted-foreground)]">They can fill them in on their partner page</div>
          </Card>
        </div>
      )}
      <Card className="overflow-x-auto">
        <div className="flex flex-wrap items-center gap-2 border-b p-3 text-sm">
          <label className="flex items-center gap-2">
            <Checkbox checked={held} onChange={(e) => setHeld(e.target.checked)} /> Also show partners whose money is still held
          </label>
        </div>
        {q.isLoading && <Skeleton className="m-3 h-32" />}
        {q.error && <p className="p-3 text-sm text-[var(--color-danger-fg)]">Couldn't load who to pay: {q.error.message}</p>}
        {q.data && rows.length === 0 && <Empty title={held ? "No partner has earned anything yet." : "Nobody to pay right now."}>{!held && "Tick the box to see money still held."}</Empty>}
        {rows.length > 0 && (
          <Table>
            <THead>
              <tr>
                <TH>Partner</TH>
                <TH className="text-right">Payable now</TH>
                <TH className="text-right">Held</TH>
                <TH>Next payable</TH>
                <TH>How to pay</TH>
              </tr>
            </THead>
            <tbody>
              {rows.map((r) => {
                const [label, tone] = PARTNER_STATUS[r.status] ?? [r.status, "muted" as const];
                return (
                  <TR key={r.affiliate_id}>
                    <TD>
                      <Link to="/affiliates/$affiliateId" params={{ affiliateId: String(r.affiliate_id) }} className="font-medium text-[var(--color-primary)] hover:underline">
                        {r.name || r.code}
                      </Link>
                      <div className="flex flex-wrap items-center gap-1 text-xs text-[var(--color-muted-foreground)]">
                        <span className="num">{r.code}</span>
                        {r.status !== "active" && <Badge variant={tone}>{label}</Badge>}
                      </div>
                    </TD>
                    <TD className="num text-right font-semibold">{r.payable_cents ? formatCents(r.payable_cents) : ""}</TD>
                    <TD className="num text-right">{r.held_cents ? formatCents(r.held_cents) : ""}</TD>
                    <TD className="num whitespace-nowrap text-xs">{r.next_payable_on ? formatDate(r.next_payable_on) : ""}</TD>
                    <TD className={`max-w-[22rem] text-xs ${r.missing_details ? "font-medium text-[var(--color-warning-fg)]" : ""}`}>{payTo(r)}</TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
      {syncing && <PartnerSyncDialog onClose={() => setSyncing(false)} />}
    </div>
  );
}

/** keap_partners_sync's answer: what was (or, in the dry run, would be) added. */
export interface PartnerSync {
  stats?: Record<string, number>;
  added_codes: string[];
  added_links: string[];
  own_program_partners: number[];
}

/** keap_partners_sync: a dry run first (reads Keap, changes nothing), then confirm to add them. Admins only. */
export function PartnerSyncDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const flow = usePreviewConfirm<PartnerSync, PartnerSync>(
    (confirm) => api<PartnerSync>("/api/v1/keap/partners/sync", { json: { confirm: confirm || undefined } }),
    (r) => {
      for (const k of ["affiliates", "affiliate-links", "payouts-due"]) void qc.invalidateQueries({ queryKey: [k] });
      toast(`Added ${r.added_codes.length} partners and ${r.added_links.length} links from Keap`);
      onClose();
    },
  );
  const p = flow.preview?.details;
  const nothing = p && p.added_codes.length === 0 && p.added_links.length === 0 && p.own_program_partners.length === 0;
  const list = (xs: string[]) => (xs.length === 0 ? "none" : xs.length > 30 ? `${xs.slice(0, 30).join(", ")} and ${xs.length - 30} more` : xs.join(", "));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(600px,calc(100vw-2rem))]">
        <DialogTitle>Sync partners from Keap</DialogTitle>
        {!p ? (
          <DialogDescription>
            Brings in referral partners and partner links made in Keap since the import. Partners already in the CRM are never changed, and nobody is emailed. First a dry run
            reads Keap and shows what would be added; nothing changes until you confirm.
          </DialogDescription>
        ) : nothing ? (
          <p className="text-sm">Keap has nothing new: every partner and link is already in the CRM.</p>
        ) : (
          <div className="space-y-3">
            <ServerSays text={plainConfirmMessage(flow.preview!.message)} />
            <Facts
              rows={[
                ["New partners", list(p.added_codes)],
                ["New partner links", list(p.added_links)],
                ["Moved onto their own Keap rate", p.own_program_partners.length ? `${p.own_program_partners.length} partner${p.own_program_partners.length === 1 ? "" : "s"}` : "none"],
                ["Recruiters filled in", p.stats?.affiliates_parent_set ? String(p.stats.affiliates_parent_set) : undefined],
                ["Skipped (contact not in the CRM)", p.stats?.affiliates_no_contact ? String(p.stats.affiliates_no_contact) : undefined],
              ]}
            />
          </div>
        )}
        <Problem text={flow.problem} />
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {nothing ? "Close" : "Cancel"}
          </Button>
          {!p ? (
            <Button disabled={flow.reviewing} onClick={flow.review}>
              {flow.reviewing ? "Reading Keap…" : "Dry run"}
            </Button>
          ) : (
            !nothing && (
              <Button disabled={flow.confirming} onClick={flow.confirm}>
                Add them
              </Button>
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
